import React, { useState, useEffect } from 'react';
import { Check, Loader, ArrowUp, Crown, HelpCircle } from 'lucide-react';
import PaymentOptions from './PaymentOptions';
import { isIndianUser } from './paymentUtils';
import { apiCall } from '../../utils/api';
import SubscriptionFAQs from './SubscriptionFAQs';

const SubscriptionPlans = ({ 
  user, 
  onClose, 
  currentPlan = null, 
  appId = 'saleswit',
  onUpgrade = null,           // NEW: Universal upgrade handler from SubscriptionModal
  isUpgrading = false,        // NEW: Loading state from SubscriptionModal
  isPayPalSubscription = null // NEW: PayPal detection function from SubscriptionModal
}) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isIndian, setIsIndian] = useState(false);
  const [showFAQs, setShowFAQs] = useState(false); // New state for FAQs modal

  useEffect(() => {
    // Determine if user is in India for plan filtering
    setIsIndian(isIndianUser());
    fetchPlans();
  }, [appId]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await apiCall(`/api/subscriptions/plans?app_id=${appId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      
      const data = await response.json();
      
      // Plans will be filtered in the component render based on isIndian state
      setPlans(data.plans || []);
    } catch (err) {
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const isPlanCurrent = (planId) => {
    return currentPlan && (currentPlan.plan_id === planId || currentPlan.id === planId);
  };

  const isUpgradePlan = (plan) => {
    if (!currentPlan || !currentPlan.amount) return false;
    return Number(plan.amount) > Number(currentPlan.amount);
  };

  const isDowngradePlan = (plan) => {
    if (!currentPlan || !currentPlan.amount) return false;
    return Number(plan.amount) < Number(currentPlan.amount) && Number(plan.amount) > 0;
  };

  const formatPrice = (amount, currency = 'INR', interval = 'month') => {
    // Convert paisa to rupees for INR
    const displayAmount = amount;
    
    const formattedPrice = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(displayAmount);
    
    return `${formattedPrice}/${interval}`;
  };

  const renderFeatureCheck = (available) => {
    return available ? (
      <Check className="h-5 w-5 text-green-400" />
    ) : (
      <span className="h-5 w-5 block"></span>
    );
  };

  const handlePlanAction = async (plan) => {
    if (isPlanCurrent(plan.id)) {
      return; // Current plan, do nothing
    }

    // If we have an upgrade handler (from SubscriptionModal), use it
    if (onUpgrade && (isUpgradePlan(plan) || isDowngradePlan(plan))) {
      await onUpgrade(plan);
      return;
    }

    // Otherwise, this is a new subscription (no current plan or free plan)
    // Let PaymentOptions handle the new subscription flow
    // This will be handled by the PaymentOptions component
  };

  const renderPlanButton = (plan) => {
    const isCurrent = isPlanCurrent(plan.id);
    const isUpgrade = isUpgradePlan(plan);
    const isDowngrade = isDowngradePlan(plan);

    if (isCurrent) {
      return (
        <div className="py-2 px-4 bg-[#0c1526] text-gray-300 rounded-lg text-center text-sm flex items-center justify-center">
          <Crown className="h-4 w-4 mr-2 text-[#ff7e00]" />
          Current Plan
        </div>
      );
    }

    // If user has a paid plan and this is an upgrade/downgrade
    if (currentPlan && currentPlan.amount > 0 && (isUpgrade || isDowngrade)) {
      if (isDowngrade) {
        return (
          <button
            onClick={() => handlePlanAction(plan)}
            disabled={isUpgrading}
            className="w-full py-2 px-4 bg-yellow-700 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isUpgrading ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Request Downgrade
              </>
            )}
          </button>
        );
      } else {
        return (
          <button
            onClick={() => handlePlanAction(plan)}
            disabled={isUpgrading}
            className="w-full py-2 px-4 bg-gradient-to-r from-[#ff7e00] to-[#e11d48] hover:shadow-lg text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isUpgrading ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                {isPayPalSubscription && isPayPalSubscription() ? 'Upgrading PayPal...' : 'Upgrading...'}
              </>
            ) : (
              <>
                <ArrowUp className="h-4 w-4 mr-2" />
                Upgrade to {plan.name}
              </>
            )}
          </button>
        );
      }
    }

    // For new subscriptions or free plan users
    return (
      <PaymentOptions
        user={user}
        plan={plan}
        appId={appId}
        onSuccess={() => {
          // Handle successful subscription
          if (onClose) onClose();
          window.location.reload(); // Refresh to update UI
        }}
        onError={() => {
          setError('Failed to subscribe. Please try again.');
        }}
      />
    );
  };

  // Filter plans based on user's location
  const filteredPlans = plans.filter(plan => {
    // First check if it's a free plan (amount === 0 or plan name contains "Free") - exclude it
    if (plan.amount === 0 || plan.name.toLowerCase().includes('free')) {
      return false;
    }
    
    // Then apply the existing location-based filter
    return isIndian ? plan.plan_type === 'domestic' : plan.plan_type === 'international';
  });

  // Add this function for opening FAQs
  const openFAQs = (e) => {
    e.preventDefault();
    setShowFAQs(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="h-8 w-8 text-[#ff7e00] animate-spin mb-4" />
        <p className="text-gray-300">Loading subscription plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={fetchPlans}
          className="px-4 py-2 bg-[#1e293b] text-white rounded hover:bg-[#263449] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (filteredPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-300">No subscription plans available at this time.</p>
      </div>
    );
  }

  return (
    <div className="subscription-plans-container">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          {currentPlan && currentPlan.amount > 0 ? 'Upgrade Your Plan' : 'Choose Your Plan'}
        </h2>
        {currentPlan && currentPlan.amount > 0 && (
          <p className="text-gray-400 text-sm">
            Currently on: {currentPlan.plan_name || currentPlan.name}
            {isPayPalSubscription && isPayPalSubscription() && (
              <span className="ml-2 px-2 py-1 bg-yellow-700 text-yellow-100 rounded text-xs">PayPal</span>
            )}
            {isPayPalSubscription && !isPayPalSubscription() && currentPlan.razorpay_subscription_id && (
              <span className="ml-2 px-2 py-1 bg-blue-700 text-blue-100 rounded text-xs">Razorpay</span>
            )}
          </p>
        )}
      </div>
      
      <div className="m-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {filteredPlans.map(plan => {
          const isCurrent = isPlanCurrent(plan.id);
          const isUpgrade = isUpgradePlan(plan);
          const isDowngrade = isDowngradePlan(plan);
          
          return (
            <div 
              key={plan.id}
              className={`bg-[#1e293b] rounded-xl shadow-md hover:shadow-lg transition-all border overflow-hidden ${
                isCurrent 
                  ? 'border-[#ff7e00] ring-2 ring-[#ff7e00] ring-opacity-50' 
                  : isUpgrade
                  ? 'border-green-500 hover:border-green-400'
                  : isDowngrade
                  ? 'border-yellow-500 hover:border-yellow-400'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              {/* Plan Header */}
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  {isCurrent && (
                    <span className="px-2 py-1 bg-[#ff7e00] text-white rounded text-xs font-medium">
                      CURRENT
                    </span>
                  )}
                  {isUpgrade && !isCurrent && (
                    <span className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium">
                      UPGRADE
                    </span>
                  )}
                  {isDowngrade && !isCurrent && (
                    <span className="px-2 py-1 bg-yellow-600 text-white rounded text-xs font-medium">
                      DOWNGRADE
                    </span>
                  )}
                </div>
                
                {plan.amount === 0 ? (
                  <div className="text-2xl font-bold text-white mb-3">Free</div>
                ) : (
                  <div className="text-2xl font-bold text-white mb-3">
                    {formatPrice(plan.amount, plan.currency, plan.interval)}
                  </div>
                )}
                
                <p className="text-gray-300 mb-4">{plan.description}</p>
                
                {/* Plan Action Button */}
                {renderPlanButton(plan)}
              </div>
              
              {/* Features List */}
              <div className="p-6">
                <h4 className="font-semibold text-white mb-4">Features</h4>
                
                <ul className="space-y-3">
                  {plan.features && Object.entries(plan.features).map(([key, value]) => {
                    // Skip boolean false values or 0 values
                    if (value === false || value === 0) return null;
                    
                    // Format the feature key for display
                    // Format the feature key for display
                    const featureName = key === 'perplexity_requests' 
                      ? 'AI Requests'  // Special case for perplexity_requests
                      : key === 'requests'
                      ? 'Reports'  // Special case for requests in SalesWit
                      : key
                          .split('_')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' '); 
                    
                    return (
                      <li key={key} className="flex items-start">
                        <span className="flex-shrink-0 mr-2 mt-0.5">
                          {renderFeatureCheck(true)}
                        </span>
                        <span className="text-gray-300">
                          {typeof value === 'boolean' 
                            ? featureName 
                            : `${featureName}: ${value}`
                          }
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Contact Support Notice - Keep this as requested */}
      {currentPlan && currentPlan.amount > 0 && (
        <div className="m-5 p-4 bg-amber-900/30 border border-amber-700 rounded-lg">
          <h4 className="text-amber-300 font-medium mb-2">Need Help?</h4>
          <p className="text-amber-100 text-sm">
            Our system automatically applies discounts or processes refunds based on your payment method. 
            If you encounter any issues during the upgrade process, our support team is here to help!
          </p>
        </div>
      )}

      {/* NEW: FAQ Link Section */}
      {currentPlan && currentPlan.amount > 0 && (
        <div className="m-5 p-4 bg-blue-900/30 border border-blue-700 rounded-lg flex justify-between items-center">
          <div>
            <h4 className="text-blue-300 font-medium mb-1">Questions about upgrading?</h4>
            <p className="text-blue-100 text-sm">
              Learn how upgrades work, how billing is calculated, and more.
            </p>
          </div>
          <button
            onClick={openFAQs}
            className="py-2 px-4 bg-blue-700 hover:bg-blue-600 text-white rounded-lg flex items-center text-sm font-medium transition-colors"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            View FAQs
          </button>
        </div>
      )}

      {/* FAQ Modal */}
      <SubscriptionFAQs 
        isOpen={showFAQs} 
        onClose={() => setShowFAQs(false)} 
      />
    </div>
  );
};

export default SubscriptionPlans;